import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import MainLayout from "../../layouts/MainLayout";
import Tabs from "../../components/Tabs";
import Alert from "../../components/Alert";
import { createLookupApi } from "../../api/lookupApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { listExamConfigs } from "../../api/examApi";
import { computeAttendanceTotals, saveAttendanceForMarksheet, listAttendanceMarksheetGroups, deleteAttendanceForMarksheet } from "../../api/studentAttendanceMarksheetApi";
import "./Attendance.css";

const periodApi = createLookupApi("periods");
const academicYearApi = createLookupApi("academic-years");

const URL_TO_TAB = {
  "student-attendance-for-marksheet": "process",
  "student-attendance-list-for-marksheet": "list",
};

const TABS = [
  { key: "process", label: "Result Attendance Process" },
  { key: "list", label: "Result Attendance Process List" },
];

/**
 * Direct port of digicampusbd's `student-attendance-for-marksheet` /
 * `student-attendance-list-for-marksheet` ("Result Attendance Process") —
 * computes each student's real attendance totals (present/absent/leave,
 * working days minus weekly/other holidays) for a date range from the
 * already-real `StudentAttendance`/`WeeklyHoliday`/`OthersHoliday` data, lets
 * staff manually adjust the computed numbers, then persists the final
 * figures per exam+section+period into the new `StudentTotalAttendance`
 * table. This is the materialized attendance summary `ExamMarksheetPage`'s
 * own doc comment flagged as missing — wiring those figures into that page
 * is a follow-up, not done automatically here.
 */
export default function StudentAttendanceMarksheetPage() {
  const { pageUrl } = useParams();
  const [tab, setTab] = useState(URL_TO_TAB[pageUrl] || "process");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    setTab(URL_TO_TAB[pageUrl] || "process");
  }, [pageUrl]);

  // ---------------- Process tab ----------------
  const [classConfigs, setClassConfigs] = useState([]);
  const [classConfigId, setClassConfigId] = useState("");
  const [examConfigs, setExamConfigs] = useState([]);
  const [examId, setExamId] = useState("");
  const [periods, setPeriods] = useState([]);
  const [periodId, setPeriodId] = useState("");
  const [academicYears, setAcademicYears] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    listClassConfigurations().then((configs) => {
      setClassConfigs(configs);
      if (configs.length > 0) setClassConfigId(String(configs[0].id));
    });
    periodApi.list().then(setPeriods);
    academicYearApi.list().then((years) => {
      setAcademicYears(years);
      if (years.length > 0) setAcademicYear(years[0].name);
    });
  }, []);

  useEffect(() => {
    const cc = classConfigs.find((c) => String(c.id) === classConfigId);
    if (!cc) return;
    listExamConfigs(cc.classId).then((configs) => {
      setExamConfigs(configs);
      setExamId(configs.length > 0 ? String(configs[0].examId) : "");
    });
  }, [classConfigId, classConfigs]);

  const handleSearch = async () => {
    setError("");
    setSuccess("");
    setRows(null);
    if (!classConfigId || !periodId || !fromDate || !toDate || !academicYear) {
      setError("Please fill in academic year, section, period, and date range.");
      return;
    }
    setLoading(true);
    try {
      const data = await computeAttendanceTotals({ classConfigId, periodId, fromDate, toDate, academicYear });
      setRows(
        data
          .slice()
          .sort((a, b) => (a.studentRoll || 0) - (b.studentRoll || 0))
          .map((r) => ({ ...r }))
      );
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to compute attendance.");
    } finally {
      setLoading(false);
    }
  };

  const updateRow = (identificationId, field, value) => {
    setRows((prev) => prev.map((r) => (r.identificationId === identificationId ? { ...r, [field]: Number(value) } : r)));
  };

  const handleProcess = async () => {
    setError("");
    setSuccess("");
    if (!rows || rows.length === 0) {
      setError("Please search students first.");
      return;
    }
    setSaving(true);
    try {
      await saveAttendanceForMarksheet({
        periodId,
        classConfigurationId: classConfigId,
        examId,
        stdattendances: rows.map((r) => ({
          studentIdentificationId: r.identificationId,
          totalPresent: r.totalPresent,
          totalAbsent: r.totalAbsent,
          totalLeave: r.totalLeave,
          fromDate,
          toDate,
          totalWorkingDays: r.totalWorkingDays,
          totalWeeklyHolidays: r.totalWeeklyHoliday,
          totalOtherHolidays: r.totalOtherHoliday,
          totalDays: r.totalDay,
        })),
      });
      setSuccess("Attendance Successfully Saved.");
      setRows(null);
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to save attendance.");
    } finally {
      setSaving(false);
    }
  };

  // ---------------- List tab ----------------
  const [groups, setGroups] = useState(null);
  const [listLoading, setListLoading] = useState(false);

  const loadGroups = () => {
    setListLoading(true);
    listAttendanceMarksheetGroups()
      .then(setGroups)
      .catch(() => setError("Failed to load processed attendance list."))
      .finally(() => setListLoading(false));
  };

  useEffect(() => {
    if (tab === "list") loadGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const handleDelete = async (g) => {
    setError("");
    setSuccess("");
    try {
      await deleteAttendanceForMarksheet(g.classConfigId, g.examId, g.periodId);
      setSuccess("Attendance Successfully Deleted.");
      loadGroups();
    } catch (err) {
      setError("Failed to delete.");
    }
  };

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Result Attendance Process</h1>
      </div>

      <Tabs tabs={TABS} active={tab} onChange={setTab} />

      {error && <Alert type="error">{error}</Alert>}
      {success && <Alert type="success">{success}</Alert>}

      {tab === "process" && (
        <div>
          <div className="attendance-filters">
            <label>
              Academic Year
              <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
                {academicYears.map((y) => (
                  <option key={y.id} value={y.name}>
                    {y.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Select Section
              <select value={classConfigId} onChange={(e) => setClassConfigId(e.target.value)}>
                {classConfigs.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.classConfigName}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Select Exam
              <select value={examId} onChange={(e) => setExamId(e.target.value)}>
                {examConfigs.length === 0 && <option value="">No exams configured</option>}
                {examConfigs.map((ec) => (
                  <option key={ec.examId} value={ec.examId}>
                    {ec.examName}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Period
              <select value={periodId} onChange={(e) => setPeriodId(e.target.value)}>
                <option value="">Select Period</option>
                {periods.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              From Date
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </label>
            <label>
              To Date
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </label>
            <button type="button" className="btn-primary" onClick={handleSearch}>
              Search
            </button>
          </div>

          {loading ? (
            <p>Loading...</p>
          ) : (
            rows && (
              <>
                <div className="table-scroll">
                  <table className="attendance-table">
                    <thead>
                      <tr>
                        <th>Student ID</th>
                        <th>Roll</th>
                        <th>Student Name</th>
                        <th>Total Day</th>
                        <th>WH. Holiday</th>
                        <th>OH. Holiday</th>
                        <th>Total Working Day</th>
                        <th>Present</th>
                        <th>Absent</th>
                        <th>Leave</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.identificationId}>
                          <td>{r.customStudentId}</td>
                          <td>{r.studentRoll}</td>
                          <td>{r.studentName}</td>
                          <td>{r.totalDay}</td>
                          <td>{r.totalWeeklyHoliday}</td>
                          <td>{r.totalOtherHoliday}</td>
                          <td>{r.totalWorkingDays}</td>
                          <td>
                            <input type="number" value={r.totalPresent} onChange={(e) => updateRow(r.identificationId, "totalPresent", e.target.value)} />
                          </td>
                          <td>
                            <input type="number" value={r.totalAbsent} onChange={(e) => updateRow(r.identificationId, "totalAbsent", e.target.value)} />
                          </td>
                          <td>
                            <input type="number" value={r.totalLeave} onChange={(e) => updateRow(r.identificationId, "totalLeave", e.target.value)} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {rows.length === 0 && <p>Student Not Found.</p>}
                <div className="form-actions">
                  <button type="button" className="btn-primary" onClick={handleProcess} disabled={saving || rows.length === 0}>
                    {saving ? "Processing..." : "Process"}
                  </button>
                </div>
              </>
            )
          )}
        </div>
      )}

      {tab === "list" && (
        <div>
          {listLoading ? (
            <p>Loading...</p>
          ) : !groups || groups.length === 0 ? (
            <p>Attendance Marksheet Not Process</p>
          ) : (
            <div className="table-scroll">
              <table className="attendance-table">
                <thead>
                  <tr>
                    <th>Exam Name</th>
                    <th>Section Name</th>
                    <th>From Date</th>
                    <th>To Date</th>
                    <th>Period Name</th>
                    <th>Delete</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map((g) => (
                    <tr key={`${g.examId}-${g.classConfigId}-${g.periodId}`}>
                      <td>{g.examName}</td>
                      <td>{g.classConfigName}</td>
                      <td>{g.fromDate}</td>
                      <td>{g.toDate}</td>
                      <td>{g.periodName}</td>
                      <td>
                        <button type="button" className="btn-danger" onClick={() => handleDelete(g)}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </MainLayout>
  );
}
